// FallbackBusSocket — WebRTC first, WebSocket if WebRTC can't connect.
//
// The default transport (main.ts) wraps the two real transports in this: it
// tries the WebRTC socket, and if that fails to *connect* — signaling error,
// ICE failure, or a hang past the timeout — it transparently switches to a
// WebSocket, all behind the one BusSocket surface BusConnection drives. The bus
// (and the script) never learn which transport won; they just see a socket that
// eventually opens.
//
// Fallback is a CONNECT-time affair only. Once a transport has opened (the
// connect handoff has gone out on it), a later drop is an ordinary disconnect,
// not a fallback trigger — matching "fall back if it fails to connect", not
// "fail over mid-session". A reconnect (the user reloading / re-hopping) starts
// the WebRTC-first race afresh.

import type { BusSocket, BusSocketFactory } from "./bus-connection.js";

const CONNECTING = 0;
const OPEN = 1;
const CLOSED = 3;

// Backstop for a primary (WebRTC) attempt that neither opens nor errors. Fast
// failures (signaling error, ICE `failed`) fall back via the sub-socket's
// close/error long before this fires; it only bounds a silent hang. Set well
// above a realistic successful WebRTC connect (gather + signal + ICE + DTLS,
// ~1–3s) so a slow-but-working connection is never cut off.
const PRIMARY_CONNECT_TIMEOUT_MS = 8000;

type Listener = (ev: { data?: unknown }) => void;

export class FallbackBusSocket implements BusSocket {
  readyState = CONNECTING;
  binaryType = "arraybuffer";

  private readonly listeners = new Map<string, Listener[]>();
  // The transport we've committed to (opened). Null until one opens.
  private active: BusSocket | null = null;
  // The attempt currently in flight — only it may commit; a late event from a
  // superseded attempt (e.g. the primary opening just after we fell back) is
  // ignored because it is no longer `attempting`.
  private attempting: BusSocket | null = null;
  private committed = false;
  private fellBack = false;
  private userClosed = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly url: string,
    private readonly makePrimary: BusSocketFactory,
    private readonly makeFallback: BusSocketFactory,
    // Called once when the primary is abandoned for the fallback. The factory
    // uses it to stop trying WebRTC for the rest of the session (see main.ts),
    // so only the first connection pays the failover wait.
    private readonly onFallback: (reason: string) => void = () => {},
  ) {
    this.beginAttempt(this.makePrimary, true);
    this.timer = setTimeout(() => this.fallback("timeout"), PRIMARY_CONNECT_TIMEOUT_MS);
  }

  private beginAttempt(make: BusSocketFactory, isPrimary: boolean): void {
    const s = make(this.url);
    s.binaryType = this.binaryType;
    this.attempting = s;

    s.addEventListener("open", () => {
      if (this.committed || this.userClosed || s !== this.attempting) return;
      this.commit(s);
    });
    s.addEventListener("message", (ev) => {
      if (this.active === s) this.emit("message", ev);
    });
    s.addEventListener("close", () => {
      if (this.committed) {
        // The committed transport dropped — an ordinary disconnect.
        if (this.active === s) this.teardown();
        return;
      }
      if (this.userClosed) return;
      if (isPrimary) this.fallback("closed");
      else this.fail(); // the fallback itself failed before opening
    });
    s.addEventListener("error", () => {
      if (this.committed) {
        if (this.active === s) {
          this.emit("error", {});
          // Drive teardown ourselves rather than waiting for a `close` that may
          // never come: a committed WebRTC reliable channel can fire `error`
          // without a following `close`/connectionstatechange, and
          // BusConnection only clears `opened` on `close` — so without this the
          // bus would stay open-but-dead, silently dropping every send. teardown
          // is idempotent, so a real `close` that DOES follow is a no-op.
          this.teardown();
        }
        return;
      }
      if (this.userClosed) return;
      // A pre-open error on the primary → fall back now. On the fallback, wait
      // for its `close` to declare total failure.
      if (isPrimary) this.fallback("error");
    });
  }

  // Terminal close of the committed transport. Idempotent (guards on CLOSED) so
  // the committed error+close pair, or a duplicate close, collapses to a single
  // bus `close`. Closes the underlying socket too — harmless when it's already
  // gone (that's what triggered us), decisive when an error fired without one.
  private teardown(): void {
    if (this.readyState === CLOSED) return;
    this.readyState = CLOSED;
    try { this.active?.close(); } catch { /* already gone */ }
    this.emit("close", {});
  }

  private commit(s: BusSocket): void {
    this.committed = true;
    this.active = s;
    this.clearTimer();
    this.readyState = OPEN;
    this.emit("open", {});
  }

  private fallback(reason: string): void {
    if (this.committed || this.fellBack || this.userClosed) return;
    this.fellBack = true;
    this.clearTimer();
    const p = this.attempting; // the primary
    if (p) { try { p.close(); } catch { /* already gone */ } }
    this.onFallback(reason);
    this.beginAttempt(this.makeFallback, false);
  }

  // Both transports failed before opening — a genuine connect failure. Surface
  // it as error+close so the supervisor reports it (the bus never opened).
  private fail(): void {
    if (this.readyState === CLOSED) return;
    this.readyState = CLOSED;
    this.emit("error", {});
    this.emit("close", {});
  }

  send(data: Uint8Array): void {
    this.active?.send(data);
  }

  sendUnreliable(data: Uint8Array): void {
    const a = this.active;
    if (!a) return;
    // Delegate to the committed transport's unreliable path when it has one
    // (WebRTC); over the WebSocket fallback this correctly degrades to send().
    if (a.sendUnreliable) a.sendUnreliable(data);
    else a.send(data);
  }

  close(): void {
    this.userClosed = true;
    this.clearTimer();
    this.readyState = CLOSED;
    const inFlight = this.attempting;
    // Close whichever sockets exist (the in-flight attempt and/or the committed
    // one — they're the same object once committed).
    if (inFlight && inFlight !== this.active) { try { inFlight.close(); } catch { /* gone */ } }
    try { this.active?.close(); } catch { /* gone */ }
  }

  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: Listener,
  ): void {
    const arr = this.listeners.get(type);
    if (arr) arr.push(listener);
    else this.listeners.set(type, [listener]);
  }

  private emit(type: string, ev: { data?: unknown }): void {
    const arr = this.listeners.get(type);
    if (!arr) return;
    for (const l of [...arr]) l(ev);
  }

  private clearTimer(): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  }
}
